import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  type JgsStudentData,
  fetchChatThreads,
  type ThreadListItem,
  fetchThreadMessages,
  RateLimitError,
  fetchCurrentUser,
} from "@/services/api";
import { useBranding } from "@/hooks/useBranding";
import iconImage from "@/assets/icon.png";
import { ChatContainer, type ChatContainerHandle } from "@/components/ChatContainer";
import Sidebar from "@/components/Sidebar";
import WelcomeMessage from "@/components/WelcomeMessage";
import ChatInput from "@/components/ChatInput";
import ChatMessage from "@/components/ChatMessage";
import TwoPaneLayout from "@/components/TwoPaneLayout";
import formatConversationText from "@/utils/formatConversationText";
import { useScrollFollow } from "@/hooks/useScrollFollow";
import { useEndUser } from "@/contexts/EndUserContext";
import JgsLinkBanner from "@/components/JgsLinkBanner";
import { fetchPipeline, addToPipeline, removePipelineEntry } from "@/services/pipeline";
import { fetchSchoolById } from "@/services/schools";
import { useFeatureTips, type FeatureTip } from "@/hooks/useFeatureTips";
import FeatureTipStrip from "@/components/FeatureTipStrip";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface StoredChat {
  threadId: string;
  title: string;
  lastUpdated: number;
  messages: Message[];
}

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const autoPromptRef = useRef<string | null>((location.state as any)?.autoPrompt || null);
  const autoThreadTitleRef = useRef<string | null>((location.state as any)?.threadTitle || null);
  const pendingThreadTitleRef = useRef<string | null>(null);
  const autoPromptFired = useRef(false);
  const [autoPromptLoading, setAutoPromptLoading] = useState(!!(location.state as any)?.autoPrompt);
  const {
    authState,
    setAuthState,
    updatePlayerSummary,
    setChatHistory: setContextChatHistory,
    isSidebarOpen,
    closeSidebar,
    openSidebar,
    userType,
    setUserType,
    setOnShare,
    currentUser,
    refreshUserData,
  } = useEndUser();

  const { branding } = useBranding();
  const coachIcon = branding?.chatIdleIconUrl ?? iconImage;

  const [messages, setMessages] = useState<Message[]>([]);
  const [storedChats, setStoredChats] = useState<StoredChat[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const currentThreadIdRef = useRef(currentThreadId);
  useEffect(() => {
    currentThreadIdRef.current = currentThreadId;
  }, [currentThreadId]);
  const [chatKey, setChatKey] = useState<string>("init");
  const [isTyping, setIsTyping] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const statusTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [isChatReady, setIsChatReady] = useState(false);
  const [studentInfo, setStudentInfo] = useState<JgsStudentData | null>(null);
  const [profileDisplayName, setProfileDisplayName] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [streamingThreadId, setStreamingThreadId] = useState<string | null>(null);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  // Server-truth flag for whether the user is linked
  const [serverLinked, setServerLinked] = useState<boolean | null>(null);
  const chatRef = useRef<ChatContainerHandle>(null);
  // Coach state
  const [linkedProfiles, setLinkedProfiles] = useState<
    Array<{ jgsProfileId: string; studentId?: string; displayName?: string; jgsData?: any }>
  >([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  // Player JGS banner visibility
  const [showJgsBanner, setShowJgsBanner] = useState(true);
  // Onboarding state — captured in ref so it survives location.state clearing.
  // Stays true until the first assistant message arrives, then permanently false.
  const isOnboardingRef = useRef(!!(location.state as any)?.isOnboarding);
  const [isOnboarding, setIsOnboarding] = useState(isOnboardingRef.current);
  const confettiFired = useRef(false);
  const [suggestionsUsed, setSuggestionsUsed] = useState(false);

  // Turn off onboarding welcome once the coach actually responds
  useEffect(() => {
    if (isOnboarding && messages.some(m => m.role === "assistant")) {
      setIsOnboarding(false);
    }
  }, [isOnboarding, messages]);

  // Pipeline state for star-from-chat
  const [pipelineIds, setPipelineIds] = useState<Set<string>>(new Set());
  const [pipelineEntryMap, setPipelineEntryMap] = useState<Map<string, string>>(new Map()); // teamId → entry _id

  // Scroll management via hook
  const { scrollRef, sentinelRef, autoFollow, handleScroll, scrollToBottom } = useScrollFollow(64);

  // Fire confetti when first coach message appears after onboarding
  useEffect(() => {
    if (isOnboarding && !confettiFired.current && messages.some(m => m.role === "assistant")) {
      confettiFired.current = true;
      confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 } });
    }
  }, [isOnboarding, messages]);

  // ─── Clear autoPrompt from location state to prevent re-fire on back/forward ───
  useEffect(() => {
    if (autoPromptRef.current) {
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Auto-send prompt from Home checklist buttons ───
  // Waits for chats to load, finds or creates the right thread, then sends.
  useEffect(() => {
    const prompt = autoPromptRef.current;
    if (!prompt) return;
    if (autoPromptFired.current) return;
    if (isLoadingChats) return;

    const title = autoThreadTitleRef.current;
    const existing = title ? storedChats.find(c => c.title === title) : null;

    // Step 1: Switch to existing thread if needed
    if (existing && currentThreadId !== existing.threadId) {
      setIsChatReady(false);
      persistCurrentThread(existing.threadId);
      setMessages(existing.messages || []);
      setChatKey(`thread-${existing.threadId}`);
      return;
    }

    // Step 2: Start a fresh chat if we're on the wrong thread
    if (!existing && (messages.length > 0 || currentThreadId)) {
      if (title) pendingThreadTitleRef.current = title;
      setIsChatReady(false);
      setMessages([]);
      persistCurrentThread(null);
      setChatKey(`auto-prompt-${Date.now()}`);
      return;
    }

    // Step 3: Stash desired title for thread creation callback
    if (!existing && title) {
      pendingThreadTitleRef.current = title;
    }

    // Step 4: Wait for ChatContainer to be ready after any remount
    if (!isChatReady) return;

    // Step 5: Fire the prompt
    autoPromptFired.current = true;
    autoPromptRef.current = null;
    handleSendMessage(prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingChats, isChatReady, currentThreadId, messages.length]);

  // Clear autoPrompt loading indicator once messages appear
  useEffect(() => {
    if (autoPromptLoading && messages.length > 0) {
      setAutoPromptLoading(false);
    }
  }, [autoPromptLoading, messages.length]);

  // Build namespaced localStorage keys per user and JGS link
  const buildStorageKeys = (userId?: string | null, hasJgsMapping?: boolean) => {
    let jgsKey: string = "none";
    try {
      const fromSession = sessionStorage.getItem("recruit_ai_jgs_key");
      if (fromSession && fromSession.trim()) jgsKey = fromSession.trim();
      else jgsKey = hasJgsMapping ? "mapped" : "none";
    } catch {
      jgsKey = hasJgsMapping ? "mapped" : "none";
    }
    const uid = userId && String(userId).trim() ? String(userId).trim() : "anon";
    return {
      chatsKey: `recruit_ai_chats:u:${uid}:jgs:${jgsKey}`,
      currentThreadKey: `recruit_ai_current_thread:u:${uid}:jgs:${jgsKey}`,
    };
  };

  // Derive a stable JGS key from student info
  const deriveJgsKey = (info: JgsStudentData | null, hasJgsMapping?: boolean): string => {
    const candidates = [
      (info as any)?.jgs_id,
      (info as any)?.id,
      (info as any)?.player_id,
      info?.player_name,
    ].map(v => (v == null ? "" : String(v)));
    const raw = candidates.find(Boolean) || (hasJgsMapping ? "mapped" : "none");
    return raw
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "-")
      .slice(0, 64);
  };

  // ─── Process currentUser from layout on mount ───
  useEffect(() => {
    if (!currentUser) return;

    const meUserType = (currentUser as any)?.userType || "player";
    // userType is already set by layout, but we update local coach state

    if (meUserType === "coach") {
      const profiles = (currentUser as any)?.linkedProfiles || [];
      setLinkedProfiles(profiles);
      if (profiles.length > 0 && !activeProfileId) {
        let savedProfile: string | null = null;
        try {
          savedProfile = sessionStorage.getItem("recruit_ai_coach_active_profile");
        } catch {}
        const hasValidSaved =
          savedProfile && profiles.some((p: any) => p.jgsProfileId === savedProfile);
        const profileToActivate = hasValidSaved ? savedProfile! : profiles[0].jgsProfileId;
        setActiveProfileId(profileToActivate);
        const activeProfile = profiles.find((p: any) => p.jgsProfileId === profileToActivate);
        if (activeProfile) {
          const jgs = activeProfile.jgsData || null;
          if (jgs) setStudentInfo(jgs as any);
          if (activeProfile.displayName) setProfileDisplayName(activeProfile.displayName);
        }
      }
      setServerLinked(profiles.length > 0);
    } else {
      const info = currentUser?.jgs_student_data || null;
      try {
        const profile = (currentUser as any)?.profile || null;
        const dn = profile?.displayName ? String(profile.displayName).trim() : "";
        if (dn) setProfileDisplayName(dn);
      } catch {}
      const has =
        typeof (currentUser as any)?.has_jgs_mapping !== "undefined"
          ? !!(currentUser as any).has_jgs_mapping
          : !!(currentUser as any)?.hasJgsMapping;
      setServerLinked(has);
      try {
        sessionStorage.setItem("recruit_ai_has_jgs", String(has));
        if (!has) sessionStorage.removeItem("recruit_ai_jgs_key");
      } catch {}
      if (info) {
        setStudentInfo(info as any);
        try {
          const key = deriveJgsKey(info as any, currentUser?.has_jgs_mapping);
          sessionStorage.setItem("recruit_ai_jgs_key", key);
        } catch {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // ─── Push player summary updates to context ───
  useEffect(() => {
    updatePlayerSummary({
      playerName: profileDisplayName || studentInfo?.player_name || "Player Name",
      jgsRank: studentInfo?.rank?.toString() || "N/A",
      classRank: studentInfo?.class_rank?.toString() || "N/A",
      classYear: studentInfo?.class_year?.toString() || "N/A",
      scoringDifferential:
        typeof studentInfo?.scoring_differential === "number"
          ? studentInfo.scoring_differential.toFixed(2)
          : studentInfo?.scoring_differential || "N/A",
    });
  }, [profileDisplayName, studentInfo, updatePlayerSummary]);

  // ─── Push isLinked to context ───
  const isLinked =
    userType === "coach"
      ? linkedProfiles.length > 0 && activeProfileId !== null
      : serverLinked !== null
        ? serverLinked
        : !!(authState?.hasJgsMapping || studentInfo);

  useEffect(() => {
    updatePlayerSummary({ isLinked });
  }, [isLinked, updatePlayerSummary]);

  // Feature tips — server-driven next best action
  const { currentTip, discoveryTips, dismissTip } = useFeatureTips({
    userId: authState?.userId,
  });

  // ─── Push storedChats to context chat history ───
  useEffect(() => {
    setContextChatHistory(
      storedChats
        .sort((a, b) => b.lastUpdated - a.lastUpdated)
        .map(c => ({
          id: c.threadId,
          title: c.title || "New Chat",
          timestamp: new Date(c.lastUpdated).toLocaleString(),
          messageCount: c.messages?.length,
        }))
    );
  }, [storedChats, setContextChatHistory]);

  // Server-first load: try server, fallback to localStorage on error
  const loadedThreadsRef = useRef(false);
  const loadThreadsCancelledRef = useRef(false);
  useEffect(() => {
    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    const loadServerThreads = async () => {
      if (!authState?.userId || loadedThreadsRef.current) return;
      loadedThreadsRef.current = true;
      loadThreadsCancelledRef.current = false;

      // Read localStorage upfront for fallback
      let localChats: StoredChat[] = [];
      let lastThread: string | null = null;
      const { chatsKey, currentThreadKey } = buildStorageKeys(
        authState?.userId,
        authState?.hasJgsMapping
      );
      try {
        const raw = localStorage.getItem(chatsKey);
        if (raw) {
          const parsed: StoredChat[] = JSON.parse(raw);
          localChats = Array.isArray(parsed) ? parsed : [];
        }
        lastThread = localStorage.getItem(currentThreadKey);
      } catch {}

      try {
        // Try to fetch from server first
        const threads: ThreadListItem[] = await fetchChatThreads(
          1,
          20,
          userType === "coach" && activeProfileId ? activeProfileId : undefined
        );
        if (loadThreadsCancelledRef.current) return;
        if (Array.isArray(threads) && threads.length > 0) {
          // Merge with existing local stored chats, preserving any stored messages
          const byId: Record<string, StoredChat> = Object.fromEntries(
            localChats.map(c => [c.threadId, c])
          );
          const merged: StoredChat[] = threads.map(t => ({
            threadId: t.threadId,
            title: t.title || byId[t.threadId]?.title || "New Chat",
            lastUpdated: t.lastUpdated || byId[t.threadId]?.lastUpdated || Date.now(),
            messages: byId[t.threadId]?.messages || [],
          }));
          // Include any local-only threads not returned by server
          for (const local of localChats) {
            if (!merged.find(m => m.threadId === local.threadId)) merged.push(local);
          }
          persistChats(merged);

          // Restore last active thread, or auto-select most recent thread with messages
          const restoredThread = lastThread ? merged.find(c => c.threadId === lastThread) : null;
          const autoThread =
            restoredThread ||
            merged.find(c => (c.messages?.length ?? 0) > 0) ||
            (threads.find(t => (t.messageCount ?? 0) > 0) ? merged[0] : null);
          if (autoThread && !loadThreadsCancelledRef.current) {
            setCurrentThreadId(autoThread.threadId);
            setChatKey(`thread-${autoThread.threadId}`);

            if (autoThread.messages?.length) {
              setMessages(autoThread.messages);
            } else {
              // Fetch messages for the auto-selected thread
              try {
                const res = await fetchThreadMessages(autoThread.threadId, 50);
                if (!loadThreadsCancelledRef.current) {
                  const mappedMsgs: Message[] = res.messages.map(m => ({
                    id: m.id,
                    role: m.role,
                    content: m.text,
                  }));
                  setMessages(mappedMsgs);
                  persistChats(prev => [
                    {
                      threadId: autoThread.threadId,
                      title: autoThread.title || "New Chat",
                      lastUpdated: autoThread.lastUpdated || Date.now(),
                      messages: mappedMsgs,
                    },
                    ...prev.filter(c => c.threadId !== autoThread.threadId),
                  ]);
                }
              } catch {
                setMessages([]);
              }
            }
          }

          // Preload messages for top 3 most recent threads with no cached messages and with messageCount > 0
          const toPreload = [...threads]
            .sort((a, b) => (b.lastUpdated || 0) - (a.lastUpdated || 0))
            .filter(t => (t.messageCount ?? 0) > 0 && !byId[t.threadId]?.messages?.length)
            .slice(0, 3);

          for (const t of toPreload) {
            if (loadThreadsCancelledRef.current) break;
            // Small spacing between requests
            await sleep(120);
            try {
              const res = await fetchThreadMessages(t.threadId, 50);
              const mappedMsgs: Message[] = res.messages.map(m => ({
                id: m.id,
                role: m.role,
                content: m.text,
              }));
              persistChats(prev => [
                {
                  threadId: t.threadId,
                  title: t.title || "New Chat",
                  lastUpdated: t.lastUpdated || Date.now(),
                  messages: mappedMsgs,
                },
                ...prev.filter(c => c.threadId !== t.threadId),
              ]);
            } catch (e: any) {
              if (e instanceof RateLimitError) {
                const retryAfter = (e.retryAfterSeconds ?? 1) * 1000;
                await sleep(retryAfter);
              }
            }
          }
        } else {
          // Server returned empty - use localStorage fallback
          if (localChats.length > 0) {
            setStoredChats(localChats);
            const fallback =
              (lastThread ? localChats.find(c => c.threadId === lastThread) : null) ||
              localChats.find(c => c.messages?.length > 0);
            if (fallback) {
              setCurrentThreadId(fallback.threadId);
              setMessages(fallback.messages || []);
              setChatKey(`thread-${fallback.threadId}`);
            }
          }
        }
      } catch (error) {
        // Server failed - fallback to localStorage
        console.warn("Failed to load from server, using localStorage fallback", error);
        if (localChats.length > 0) {
          setStoredChats(localChats);
          const fallback =
            (lastThread ? localChats.find(c => c.threadId === lastThread) : null) ||
            localChats.find(c => c.messages?.length > 0);
          if (fallback) {
            setCurrentThreadId(fallback.threadId);
            setMessages(fallback.messages || []);
            setChatKey(`thread-${fallback.threadId}`);
          }
        }
      } finally {
        setIsLoadingChats(false);
      }
    };
    loadServerThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.userId]);

  const persistChats = useCallback(
    (updater: StoredChat[] | ((prev: StoredChat[]) => StoredChat[])) => {
      setStoredChats(prev => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        try {
          const { chatsKey } = buildStorageKeys(authState?.userId, authState?.hasJgsMapping);
          localStorage.setItem(chatsKey, JSON.stringify(next));
        } catch {}
        return next;
      });
    },
    [authState?.userId, authState?.hasJgsMapping]
  );

  const persistCurrentThread = (tid: string | null) => {
    setCurrentThreadId(tid);
    try {
      const { currentThreadKey } = buildStorageKeys(authState?.userId, authState?.hasJgsMapping);
      if (tid) localStorage.setItem(currentThreadKey, tid);
      else localStorage.removeItem(currentThreadKey);
    } catch {}
  };

  // Follow on any new messages when at bottom (autoFollow true)
  useLayoutEffect(() => {
    if (autoFollow) {
      // Use "auto" to avoid jank when many nodes append rapidly
      scrollToBottom("auto");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, autoFollow]);

  const clearStatusTimers = useCallback(() => {
    statusTimersRef.current.forEach(clearTimeout);
    statusTimersRef.current = [];
  }, []);

  const startThinkingStatus = useCallback(() => {
    clearStatusTimers();
    setStatusMessage("Thinking...");
    statusTimersRef.current.push(
      setTimeout(() => setStatusMessage("Reading the green..."), 6000),
      setTimeout(() => setStatusMessage("Lining up the shot..."), 12000),
      setTimeout(() => setStatusMessage("Checking the yardage book..."), 18000)
    );
  }, [clearStatusTimers]);

  const startFunctionStatus = useCallback(
    (message: string) => {
      clearStatusTimers();
      setStatusMessage(message);
      statusTimersRef.current.push(
        setTimeout(() => setStatusMessage("Making sure we're not missing any good fits..."), 15000),
        setTimeout(() => setStatusMessage("Leaving no stone unturned..."), 30000)
      );
    },
    [clearStatusTimers]
  );

  const clearStatus = useCallback(() => {
    clearStatusTimers();
    setStatusMessage("");
  }, [clearStatusTimers]);

  // Clear status whenever typing stops
  useEffect(() => {
    if (!isTyping) clearStatus();
  }, [isTyping, clearStatus]);

  const handleNewChat = async () => {
    // Stop any in-flight response when starting a new chat
    if (isStreaming || isCancelling) {
      try {
        await chatRef.current?.stop();
      } catch {}
      setIsTyping(false);
    }
    if (messages.length === 0) return; // Already on welcome/new chat state
    // Cancel any in-flight loadServerThreads so it doesn't restore an old thread
    loadThreadsCancelledRef.current = true;
    setMessages([]);
    // Close mobile sidebar after starting a new chat
    closeSidebar();
    persistCurrentThread(null);
    setIsLoadingThread(false);
    // Re-mount ChatContainer to reset internal thread state so next send creates/uses a fresh thread
    setChatKey(`new-${Date.now()}`);
  };

  const handleSelectChat = async (threadId: string) => {
    const found = storedChats.find(c => c.threadId === threadId);
    if (!found) return;

    // Stop any in-flight response when switching chats
    if (isStreaming || isCancelling) {
      try {
        await chatRef.current?.stop();
      } catch {}
      setIsTyping(false);
    }

    setIsLoadingThread(true);
    setIsTyping(false);
    persistCurrentThread(threadId);

    const useCached = Array.isArray(found.messages) && found.messages.length > 0;

    if (useCached) {
      // Re-mount with cached messages
      setTimeout(() => {
        setMessages(found.messages);
        closeSidebar();
        setIsLoadingThread(false);
        // Force remount of ChatContainer with the correct threadId
        setChatKey(`thread-${threadId}`);
      }, 0);
      return;
    }

    // No cached messages: fetch from server
    try {
      if (!authState?.userId) throw new Error("Not authenticated");
      const res = await fetchThreadMessages(threadId, 50);
      const mappedMsgs: Message[] = res.messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.text,
      }));

      // Persist fetched messages into chat history
      persistChats(prev => [
        {
          threadId,
          title: found.title || "New Chat",
          lastUpdated: found.lastUpdated,
          messages: mappedMsgs,
        },
        ...prev.filter(c => c.threadId !== threadId),
      ]);

      setMessages(mappedMsgs);
      closeSidebar();
      setIsLoadingThread(false);
      // Force remount of ChatContainer with the correct threadId
      setChatKey(`thread-${threadId}`);
    } catch (e: any) {
      console.error("Failed to load thread messages", e);
      toast.error("Failed to load messages for this chat");
      // Keep sidebar open so user can retry another thread
      setIsLoadingThread(false);
    }
  };

  const handleSendMessage = async (content: string) => {
    console.log("📤 handleSendMessage called", { content, isChatReady, hasRef: !!chatRef.current });

    // Check if cancellation is in progress
    if (chatRef.current?.isCancelling) {
      console.warn("⚠️ Cancellation in progress, message blocked");
      toast.error("Please wait, stopping previous message...");
      return;
    }

    if (!isChatReady) {
      console.warn("⚠️ Chat not ready yet, message blocked");
      toast.error("Chat is not ready yet. Please wait...");
      return;
    }

    // If another thread is streaming, block send to prevent silent failure
    if (isStreaming && streamingThreadId && streamingThreadId !== currentThreadId) {
      toast.error("Another chat is generating. Please wait or stop it first.");
      return;
    }

    setIsTyping(true);
    startThinkingStatus();
    await chatRef.current?.send(content);
  };

  const handleTipAction = useCallback(
    (tip: FeatureTip) => {
      if (tip.action.type === "prompt") {
        // Tip strip only renders when messages are loaded, so chat is functional
        // even if isChatReady hasn't re-fired after a thread switch — send directly
        setIsTyping(true);
        startThinkingStatus();
        chatRef.current?.send(tip.action.prompt);
      }
      // navigate tips are handled by the components themselves;
      // completion is now decided server-side via the next-step endpoint
    },
    [startThinkingStatus]
  );

  const handleStopStreaming = async () => {
    console.log("🛑 handleStopStreaming called");
    await chatRef.current?.stop();
    setIsTyping(false);
    toast.info("Response stopped");
  };

  const legacyCopy = (text: string): boolean => {
    try {
      // Prefer a contentEditable div selection for better iOS compatibility
      const div = document.createElement("div");
      div.contentEditable = "true";
      div.style.position = "fixed";
      div.style.top = "0";
      div.style.left = "0";
      div.style.opacity = "0";
      div.style.pointerEvents = "none";
      div.innerText = text;
      document.body.appendChild(div);
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(div);
      selection?.removeAllRanges();
      selection?.addRange(range);
      const ok = document.execCommand("copy");
      selection?.removeAllRanges();
      document.body.removeChild(div);
      return !!ok;
    } catch {
      return false;
    }
  };

  const handleShare = async () => {
    const text = formatConversationText(messages);
    if (!text) {
      toast.info("Nothing to share yet");
      return;
    }
    const canUseWebShare = typeof navigator !== "undefined" && "share" in navigator;
    if (canUseWebShare) {
      try {
        const shareData: any = { title: "golfrecruit.ai Conversation", text };
        if (
          typeof (navigator as any).canShare === "function" &&
          !(navigator as any).canShare(shareData)
        ) {
          throw new Error("Web Share cannot share this payload");
        }
        await (navigator as any).share(shareData);
        return; // success, do not fallback
      } catch (e: any) {
        const msg = String(e?.message || "").toLowerCase();
        const name = String(e?.name || "").toLowerCase();
        // If user canceled the share sheet, do not fallback to copying
        if (name === "aborterror" || msg.includes("abort") || msg.includes("cancel")) {
          return;
        }
        // else fall through to clipboard
      }
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Conversation copied to clipboard");
        return;
      }
    } catch {
      // proceed to legacy fallback
    }
    if (legacyCopy(text)) {
      toast.success("Conversation copied to clipboard");
    } else {
      toast.error("Unable to share or copy the conversation");
    }
  };

  // ─── Register share callback with header ───
  // Only offer Share once there is a conversation to share.
  useEffect(() => {
    if (messages.length === 0) {
      setOnShare(undefined);
      return;
    }
    setOnShare(() => handleShare());
    return () => setOnShare(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, setOnShare]);

  // Handle successful JGS link from JgsLinkBanner
  const handleLinkSuccess = async (profileId: string) => {
    setAuthState(prev => (prev ? { ...prev, hasJgsMapping: true } : prev));
    setServerLinked(true);
    try {
      sessionStorage.setItem("recruit_ai_has_jgs", "true");
    } catch {}
    try {
      if (authState?.userId) {
        const me = await fetchCurrentUser();
        const info = me?.jgs_student_data || null;
        if (info) setStudentInfo(info as any);
        const profile = (me as any)?.profile || null;
        if (profile?.displayName) setProfileDisplayName(profile.displayName);
      }
    } catch {}
    toast.success("Profile linked! Your AI coach now has access to your JGS stats.");
  };

  // Debug: log link state derivation
  const hasStudentInfo = !!studentInfo;
  useEffect(() => {
    try {
      console.log("[Index] link-state", {
        userType,
        serverLinked,
        authHas: authState?.hasJgsMapping,
        hasStudentInfo,
        isLinked,
        linkedProfilesCount: linkedProfiles.length,
        activeProfileId,
      });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userType,
    serverLinked,
    authState?.hasJgsMapping,
    hasStudentInfo,
    linkedProfiles.length,
    activeProfileId,
  ]);

  // Persist activeProfileId and re-fetch threads when coach switches profiles
  const prevActiveProfileRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    // First run: record the value, don't reload (initial load handled by loadServerThreads)
    if (prevActiveProfileRef.current === undefined) {
      prevActiveProfileRef.current = activeProfileId;
      return;
    }
    // Same profile: no-op
    if (prevActiveProfileRef.current === activeProfileId) return;
    prevActiveProfileRef.current = activeProfileId;

    // Persist to sessionStorage
    if (activeProfileId) {
      try {
        sessionStorage.setItem("recruit_ai_coach_active_profile", activeProfileId);
      } catch {}
    }

    if (userType !== "coach" || !authState?.userId) return;

    // Profile changed — clear chat state and reload threads for new profile
    setMessages([]);
    persistCurrentThread(null);
    setStoredChats([]);
    setChatKey(`profile-switch-${Date.now()}`);
    setIsLoadingChats(true);

    (async () => {
      try {
        const threads: ThreadListItem[] = await fetchChatThreads(
          1,
          20,
          activeProfileId || undefined
        );
        if (Array.isArray(threads) && threads.length > 0) {
          const mapped: StoredChat[] = threads.map(t => ({
            threadId: t.threadId,
            title: t.title || "New Chat",
            lastUpdated: t.lastUpdated || Date.now(),
            messages: [],
          }));
          setStoredChats(mapped);
        } else {
          setStoredChats([]);
        }
      } catch (err) {
        console.warn("[Index] Failed to load threads for profile switch", err);
        setStoredChats([]);
      } finally {
        setIsLoadingChats(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileId]);

  // ─── Pipeline: load on mount for star-from-chat ───
  const linkedJgsProfileForPipeline =
    userType === "coach" ? (activeProfileId ?? undefined) : undefined;
  useEffect(() => {
    let cancelled = false;
    async function loadPipeline() {
      try {
        const resp = await fetchPipeline({ linkedJgsProfile: linkedJgsProfileForPipeline });
        if (cancelled) return;
        const ids = new Set<string>();
        const entryMap = new Map<string, string>();
        for (const entry of resp.pipeline) {
          if (entry.school.teamId) {
            ids.add(entry.school.teamId);
            entryMap.set(entry.school.teamId, entry._id);
          }
        }
        setPipelineIds(ids);
        setPipelineEntryMap(entryMap);
      } catch (err) {
        console.error("[Index] Failed to load pipeline:", err);
      }
    }
    loadPipeline();
    return () => {
      cancelled = true;
    };
  }, [linkedJgsProfileForPipeline]);

  const handleTogglePipeline = useCallback(
    async (schoolId: string, schoolName: string) => {
      if (pipelineIds.has(schoolId)) {
        // Remove from pipeline
        const entryId = pipelineEntryMap.get(schoolId);
        if (!entryId) return;
        try {
          await removePipelineEntry(entryId, linkedJgsProfileForPipeline);
          setPipelineIds(prev => {
            const next = new Set(prev);
            next.delete(schoolId);
            return next;
          });
          setPipelineEntryMap(prev => {
            const next = new Map(prev);
            next.delete(schoolId);
            return next;
          });
          toast.success(`Removed ${schoolName} from My Schools`);
        } catch (err) {
          console.error("[Index] Failed to remove from pipeline:", err);
          toast.error("Failed to remove school");
        }
      } else {
        // Add to pipeline — fetch full school data so the card shows division/coach/etc.
        try {
          let school: {
            schoolName: string;
            teamId: string;
            division?: string | null;
            conference?: string | null;
            state?: string | null;
            headCoach?: string | null;
            headCoachEmail?: string | null;
            gender?: string | null;
          } = { schoolName, teamId: schoolId };
          try {
            const full = await fetchSchoolById(schoolId);
            school = {
              schoolName: full.schoolName || schoolName,
              teamId: full.schoolId || schoolId,
              division: full.division,
              conference: full.conference,
              state: full.state,
              headCoach: full.headCoach,
              headCoachEmail: full.headCoachEmail,
              gender: full.gender,
            };
          } catch {
            /* fall back to minimal data */
          }
          const entry = await addToPipeline(school, linkedJgsProfileForPipeline);
          setPipelineIds(prev => new Set(prev).add(schoolId));
          setPipelineEntryMap(prev => new Map(prev).set(schoolId, entry._id));
          toast.success(`Added ${schoolName} to My Schools`);
        } catch (err) {
          console.error("[Index] Failed to add to pipeline:", err);
          toast.error("Failed to add school");
        }
      }
    },
    [pipelineIds, pipelineEntryMap, linkedJgsProfileForPipeline]
  );

  return (
    <>
      {/* Hidden ChatContainer for managing chat state */}
      <ChatContainer
        key={chatKey}
        ref={chatRef}
        initialThreadId={currentThreadId}
        initialMessages={messages.map(m => ({
          id: m.id,
          sender: m.role === "user" ? "user" : "ai",
          text: m.content,
        }))}
        activeThreadIdForSends={currentThreadId}
        onThreadCreated={tid => {
          persistCurrentThread(tid);
          const pendingTitle = pendingThreadTitleRef.current;
          if (pendingTitle) {
            persistChats(prev => [
              { threadId: tid, title: pendingTitle, lastUpdated: Date.now(), messages: [] },
              ...prev.filter(c => c.threadId !== tid),
            ]);
          }
        }}
        onMessagesUpdate={(tid, msgs) => {
          const mapped: Message[] = msgs.map(m => ({
            id: m.id,
            role: m.sender === "user" ? "user" : "assistant",
            content: m.text,
          }));
          const isPlaceholderTitle = (t?: string | null) => {
            if (!t) return true;
            const norm = String(t).trim().toLowerCase();
            return norm === "new chat" || norm === "new";
          };
          const firstUser = mapped.find(m => m.role === "user");
          const firstUserText =
            firstUser?.content.replace(/^\[ONBOARDING_COMPLETE:\w+\]\s*/, "") ?? null;
          const computedFromFirstUser = firstUserText
            ? firstUserText.length > 50
              ? firstUserText.slice(0, 50) + "…"
              : firstUserText
            : null;
          const pendingTitle = pendingThreadTitleRef.current;
          if (pendingTitle) pendingThreadTitleRef.current = null;
          persistChats(prev => {
            const existing = prev.find(c => c.threadId === tid);
            const bestExistingTitle = existing?.title || pendingTitle;
            const title = !isPlaceholderTitle(bestExistingTitle)
              ? (bestExistingTitle as string)
              : computedFromFirstUser || "New Chat";
            return [
              { threadId: tid, title, lastUpdated: Date.now(), messages: mapped },
              ...prev.filter(c => c.threadId !== tid),
            ];
          });
          // Use ref to get latest currentThreadId (avoids stale closure)
          if (tid === currentThreadIdRef.current || currentThreadIdRef.current == null) {
            setMessages(mapped);
          }
        }}
        onError={m => {
          toast.error(m);
          setIsTyping(false);
        }}
        onComplete={() => setIsTyping(false)}
        onProgress={msg => {
          if (msg) startFunctionStatus(msg);
          else clearStatus();
        }}
        onReady={() => setIsChatReady(true)}
        onStudentInfo={info => {
          setStudentInfo(info);
          try {
            const key = deriveJgsKey(info, authState?.hasJgsMapping);
            sessionStorage.setItem("recruit_ai_jgs_key", key);
          } catch {}
        }}
        onOutreachDraft={outreach => {
          toast.success(`Draft saved: ${outreach.subject}`, {
            description: `To: ${outreach.recipient.coachName} — ${outreach.recipient.schoolName}`,
            action: {
              label: "View draft",
              onClick: () => navigate("/outreach"),
            },
          });
        }}
        onProfileUpdated={() => {
          refreshUserData();
        }}
        onTitleUpdate={(tid, title) => {
          persistChats(prev => [
            {
              threadId: tid,
              title: title || "New Chat",
              lastUpdated: Date.now(),
              messages: prev.find(c => c.threadId === tid)?.messages || [],
            },
            ...prev.filter(c => c.threadId !== tid),
          ]);
        }}
        onStreamingChange={(tid, streaming) => {
          setIsStreaming(streaming);
          setStreamingThreadId(streaming ? tid : null);
        }}
        onCancellingChange={(tid, cancelling) => {
          setIsCancelling(cancelling);
          if (cancelling) setStreamingThreadId(tid);
        }}
        linkedJgsProfile={userType === "coach" ? activeProfileId : null}
      />

      <TwoPaneLayout
        isSidebarOpen={isSidebarOpen}
        onCloseSidebar={closeSidebar}
        sidebar={
          <Sidebar
            chatHistory={storedChats
              .sort((a, b) => b.lastUpdated - a.lastUpdated)
              .map(c => ({
                id: c.threadId,
                title: c.title || "New Chat",
                timestamp: new Date(c.lastUpdated).toLocaleString(),
                messageCount: c.messages?.length,
              }))}
            onNewChat={handleNewChat}
            onClose={closeSidebar}
            onSelectChat={handleSelectChat}
            activeId={currentThreadId}
            disableNewChat={messages.length === 0}
            playerName={profileDisplayName || studentInfo?.player_name || "Player Name"}
            classYear={studentInfo?.class_year?.toString() || "N/A"}
            jgsRank={studentInfo?.rank?.toString() || "N/A"}
            scoringDifferential={
              typeof studentInfo?.scoring_differential === "number"
                ? studentInfo.scoring_differential.toFixed(2)
                : studentInfo?.scoring_differential || "N/A"
            }
            isLinked={isLinked}
          />
        }
        main={
          <>
            <div
              className="flex-1 min-w-0 overflow-y-auto overscroll-contain overflow-x-hidden relative"
              ref={scrollRef}
              onScroll={handleScroll}
            >
              {isOnboarding ? (
                <div className="max-w-4xl mx-auto w-full">
                  <div className="flex flex-col items-center text-center pt-12 pb-6 px-4">
                    <img
                      src={coachIcon as string}
                      alt="Your Coach"
                      className="w-16 h-16 rounded-full object-cover mb-4"
                    />
                    <h2 className="text-lg font-semibold text-foreground mb-2">
                      Welcome to golfrecruit.ai
                    </h2>
                    <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
                      Your personal recruiting coach is getting ready. In just a moment, you'll be
                      connected with an AI coach who knows the ins and outs of college golf
                      recruiting — from finding the right programs to crafting emails that get
                      coaches' attention.
                    </p>
                  </div>
                  <div className="px-4 md:px-6 py-2">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 pt-1">
                        <img
                          src={coachIcon as string}
                          alt="Coach"
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="bg-card rounded-2xl border border-border/60 shadow-sm px-5 py-4">
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                              <div
                                className="w-2 h-2 bg-primary/60 rounded-full animate-pulse"
                                style={{ animationDelay: "0ms" }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-primary/60 rounded-full animate-pulse"
                                style={{ animationDelay: "150ms" }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-primary/60 rounded-full animate-pulse"
                                style={{ animationDelay: "300ms" }}
                              ></div>
                            </div>
                            <span className="text-sm text-muted-foreground">
                              Your coach is preparing your personalized greeting...
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isLoadingChats || isLoadingThread || autoPromptLoading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                    {autoPromptLoading && (
                      <p className="mt-4 text-sm text-muted-foreground">Starting chat...</p>
                    )}
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <WelcomeMessage
                  isLinked={isLinked}
                  playerName={profileDisplayName || studentInfo?.player_name}
                  onPromptClick={handleSendMessage}
                  discoveryTips={discoveryTips}
                  onTipAction={handleTipAction}
                />
              ) : (
                <div className="max-w-4xl mx-auto w-full">
                  {/* JGS link banner removed from chat — linking now handled via onboarding and profile page */}
                  {(() => {
                    // Filter out onboarding signal messages from display
                    const visibleMessages = messages.filter(
                      m => !(m.role === "user" && m.content.startsWith("[ONBOARDING_COMPLETE:"))
                    );
                    let lastAssistantIdx = -1;
                    for (let i = visibleMessages.length - 1; i >= 0; i--) {
                      if (visibleMessages[i].role === "assistant") {
                        lastAssistantIdx = i;
                        break;
                      }
                    }
                    return visibleMessages.map((message, index) => {
                      // Show avatar only if this is the first message or the previous message was from a different role
                      const showAvatar =
                        index === 0 || visibleMessages[index - 1].role !== message.role;
                      const isLastAssistant =
                        message.role === "assistant" && index === lastAssistantIdx;
                      return (
                        <div key={message.id} className="chat-row">
                          <ChatMessage
                            role={message.role}
                            content={message.content}
                            showAvatar={showAvatar}
                            pipelineIds={pipelineIds}
                            onTogglePipeline={handleTogglePipeline}
                            isLastMessage={isLastAssistant && !isTyping && !suggestionsUsed}
                            onSuggestionClick={text => {
                              setSuggestionsUsed(true);
                              handleSendMessage(text);
                            }}
                          />
                        </div>
                      );
                    });
                  })()}
                  {isTyping && (
                    <div className="px-4 md:px-6 py-2">
                      <div className="bg-card rounded-2xl border border-border/60 shadow-sm px-5 py-4 md:px-6 md:py-5">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            <div
                              className="w-2 h-2 bg-primary/60 rounded-full animate-pulse"
                              style={{ animationDelay: "0ms" }}
                            ></div>
                            <div
                              className="w-2 h-2 bg-primary/60 rounded-full animate-pulse"
                              style={{ animationDelay: "150ms" }}
                            ></div>
                            <div
                              className="w-2 h-2 bg-primary/60 rounded-full animate-pulse"
                              style={{ animationDelay: "300ms" }}
                            ></div>
                          </div>
                          {statusMessage && (
                            <span className="text-sm text-muted-foreground">{statusMessage}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Bottom sentinel used to detect when user is at/near bottom */}
              <div ref={sentinelRef} style={{ height: 1, overflowAnchor: "auto" }} />
            </div>

            {/* Above-input tip strip */}
            {currentTip && messages.length > 0 && (
              <FeatureTipStrip tip={currentTip} onDismiss={dismissTip} onAction={handleTipAction} />
            )}

            <ChatInput
              onSend={handleSendMessage}
              onStop={handleStopStreaming}
              isStreaming={isStreaming && streamingThreadId === currentThreadId}
              isCancelling={isCancelling && streamingThreadId === currentThreadId}
              isReady={isChatReady && (userType === "player" || isLinked)}
              placeholder={
                !isLinked && userType === "player"
                  ? "Ask me anything about golf recruiting..."
                  : undefined
              }
            />
          </>
        }
      />
    </>
  );
};

export default Index;
